/**
 * Responsibility: move the camera to trail the aircraft with damped, floating motion.
 *
 * ## What the lag is for, and where it has to live
 *
 * The requirement is that pitching up lets the aircraft rise in frame before the camera
 * catches up and re-centres it, and the same for down, left, and right.
 *
 * The obvious way to get that — lag the camera's *position* — does almost nothing. Because
 * the camera always aims so that the aircraft lands at its configured screen height, moving
 * the camera behind or below its ideal spot only changes the angle it looks from; the
 * aircraft stays put in the frame. What actually produces the effect is lagging the point
 * the camera aims *at*. Both lags exist here, and they do different jobs: position lag is
 * the sense of weight, aim lag is the framing.
 *
 * ## Why the aim leads, and why only horizontally
 *
 * A bare lag would drag the aim point backwards along the flight path — at cruise speed
 * that is well over a hundred units behind the aircraft, which tilts the whole framing by
 * several degrees and makes the configured screen height meaningless. So the aim leads along
 * the aircraft's horizontal travel, which cancels that along-track lag exactly.
 *
 * The lead is applied horizontally only. Leading vertically as well cancels the vertical lag
 * just as exactly, which pins the aircraft in the frame and removes the entire effect —
 * worth knowing, because it looks like the more principled choice and is not.
 *
 * The lead follows changes of direction on its own time constant rather than instantly. In a
 * turn it therefore points slightly wide of where the aircraft is actually going, which
 * swings the aircraft sideways in frame. That is what covers "same for left and right".
 *
 * Note what this does *not* do: during a sustained climb the aircraft holds its raised
 * position in frame rather than drifting back to centre. Re-centring happens when the climb
 * stops. Making it settle mid-climb would need the lag to respond to acceleration rather
 * than velocity, which is more machinery than the effect is worth.
 *
 * ## Vertical framing
 *
 * The aircraft is asked to sit 15–20% up from the bottom of the frame, leaving room below
 * to descend into. That is done by tilting the view direction up by an angle derived from
 * the field of view, which is exact for any camera attitude — adding a world-space vertical
 * offset instead is only correct when the camera is level, and quietly misses the mark by a
 * couple of percent of frame height once the camera is looking down at the aircraft.
 *
 * On top of that sits an adjustable **downward tilt**, because in permanent night the upper
 * half of a level frame is empty black. Aiming down trades sky for terrain. It is a second
 * angle on the same axis as the framing tilt, so the two simply add — which is also why
 * raising it carries the aircraft up the frame rather than leaving it where it was.
 *
 * Damping runs on the rendered frame delta rather than the fixed simulation step, so the
 * camera stays smooth on displays that do not match the simulation rate. `core/damp.js` is
 * framerate-independent, which is what makes that safe.
 */

import * as THREE from 'three';

import { damp } from '../core/damp.js';
import { CAMERA, degToRad } from '../config.js';

/** Velocity treated as zero when none is supplied, so the lead term simply switches off. */
const NO_VELOCITY = { x: 0, y: 0, z: 0 };

/**
 * A downward tilt held inside the configured range.
 *
 * Exported so the settings store and the menu clamp identically rather than each having their own idea of the limits.
 *
 * @param {number} degrees
 * @returns {number} Degrees, within `CAMERA.MIN_PITCH_DOWN`..`CAMERA.MAX_PITCH_DOWN`.
 */
export function clampPitchDown(degrees) {
  if (!Number.isFinite(degrees)) return CAMERA.PITCH_DOWN;
  return Math.min(CAMERA.MAX_PITCH_DOWN, Math.max(CAMERA.MIN_PITCH_DOWN, degrees));
}

/**
 * Create a chase camera controller.
 *
 * @param {THREE.PerspectiveCamera} camera
 * @param {object} [options]
 * @param {(x: number, z: number) => number} [options.sampleGround] - When supplied, the
 *   camera is kept clear of the terrain behind the aircraft. Should be the *drawn* surface,
 *   so the camera is not lifted over ridges that are not on screen.
 * @returns {{update: Function, snapTo: Function, getPosition: Function}}
 */
export function createChaseCamera(camera, { sampleGround, pitchDown = CAMERA.PITCH_DOWN } = {}) {
  if (!camera || !camera.isCamera) {
    throw new Error('createChaseCamera: a Three.js camera is required');
  }

  /*
    Held here rather than read from config each frame, so the setting can be changed while flying. Clamped on the way in,
    so the range in `config.js` is enforced in one place however the value arrives — from a stored preference, a keypress,
    or a caller.
  */
  let downTilt = clampPitchDown(pitchDown);

  /*
    Scratch vectors reused every frame. A chase camera runs on every rendered frame, so
    allocating here would produce garbage at display rate for no reason.
  */
  const position = new THREE.Vector3();
  const aim = new THREE.Vector3();
  const ideal = new THREE.Vector3();
  const aimTarget = new THREE.Vector3();
  const toAim = new THREE.Vector3();
  /** Horizontal travel the aim point leads along, following direction changes slowly. */
  const lead = new THREE.Vector3();
  const forward = new THREE.Vector3();
  const right = new THREE.Vector3();
  const up = new THREE.Vector3();
  const worldUp = new THREE.Vector3(0, 1, 0);

  /** The camera's own bank, which follows a fraction of the aircraft's. */
  let roll = 0;

  /**
   * Where the camera would sit with no lag at all.
   *
   * Behind is the opposite of the aircraft's horizontal facing. At heading 0 the aircraft
   * faces negative Z, so behind is positive Z. Deliberately independent of pitch, which is
   * what leaves the vertical framing free to drift and settle.
   */
  function idealPositionFor(state, target) {
    return target.set(
      state.x - Math.sin(state.heading) * CAMERA.DISTANCE_BEHIND,
      state.y + CAMERA.HEIGHT_ABOVE,
      state.z + Math.cos(state.heading) * CAMERA.DISTANCE_BEHIND,
    );
  }

  /**
   * Where the aim point is heading.
   *
   * Horizontally: ahead of the aircraft by one aim time constant of travel, which cancels
   * the along-track lag. Vertically: the aircraft itself, so the vertical lag survives and
   * produces the climb and dive movement in frame.
   */
  function aimTargetFor(state, target) {
    return target.set(
      state.x + lead.x * CAMERA.AIM_TIME_CONSTANT,
      state.y,
      state.z + lead.z * CAMERA.AIM_TIME_CONSTANT,
    );
  }

  /** Keep a point clear of the terrain below it. */
  function liftClearOfGround(target) {
    if (typeof sampleGround !== 'function') return;

    const floor = sampleGround(target.x, target.z) + CAMERA.MIN_GROUND_CLEARANCE;
    if (target.y < floor) {
      target.y = floor;
    }
  }

  /**
   * Point the camera so the aim point lands at the configured screen height, banked.
   *
   * The tilt is applied to the direction rather than by offsetting the aim point in world
   * space, which makes the resulting screen position exact rather than approximate.
   */
  function look() {
    toAim.subVectors(aim, position);

    /*
      Tilt the view up by the angle that puts the aim point at the requested fraction of
      frame height. At a fraction of 0.5 this is zero and the aim point is centred.

      The adjustable downward tilt is subtracted from the same angle rather than applied as a
      separate rotation. Both turn about the horizontal `right` axis, so they compose by
      addition — and doing it in one step means there is only one place the sign can be wrong.
    */
    const halfFov = degToRad(camera.fov) / 2;
    const framing = Math.atan(Math.tan(halfFov) * (1 - 2 * CAMERA.AIRCRAFT_SCREEN_HEIGHT));
    const tilt = framing - degToRad(downTilt);

    right.crossVectors(toAim, worldUp);
    if (right.lengthSq() > 0) {
      right.normalize();
      toAim.applyAxisAngle(right, tilt);
    }

    /*
      Bank by rotating the up vector about the view direction. Setting a rotation after
      lookAt would not work — lookAt overwrites rotation entirely.
    */
    forward.copy(toAim).normalize();
    up.copy(worldUp).applyAxisAngle(forward, roll);

    camera.position.copy(position);
    camera.up.copy(up);
    camera.lookAt(position.x + toAim.x, position.y + toAim.y, position.z + toAim.z);
  }

  return {
    /**
     * Advance the camera one rendered frame.
     *
     * @param {{x: number, y: number, z: number, heading: number, roll: number}} state
     *   Aircraft state, normally the interpolated one.
     * @param {number} dt - Seconds since the previous rendered frame.
     * @param {{x: number, y: number, z: number}} [velocity] - Aircraft velocity in world
     *   units per second, from the flight model. Without it the aim lag becomes permanent
     *   rather than settling.
     */
    update(state, dt, velocity = NO_VELOCITY) {
      lead.set(
        damp(lead.x, velocity.x, dt, CAMERA.AIM_LEAD_TIME_CONSTANT),
        0,
        damp(lead.z, velocity.z, dt, CAMERA.AIM_LEAD_TIME_CONSTANT),
      );

      idealPositionFor(state, ideal);
      liftClearOfGround(ideal);

      position.set(
        damp(position.x, ideal.x, dt, CAMERA.POSITION_TIME_CONSTANT),
        damp(position.y, ideal.y, dt, CAMERA.POSITION_TIME_CONSTANT),
        damp(position.z, ideal.z, dt, CAMERA.POSITION_TIME_CONSTANT),
      );

      /*
        Clear of the ground again after damping. The eased position is not on the line
        between two safe points in any useful sense, so checking only the ideal would still
        let the camera cut a corner through a ridge.
      */
      liftClearOfGround(position);

      aimTargetFor(state, aimTarget);
      aim.set(
        damp(aim.x, aimTarget.x, dt, CAMERA.AIM_TIME_CONSTANT),
        damp(aim.y, aimTarget.y, dt, CAMERA.AIM_TIME_CONSTANT),
        damp(aim.z, aimTarget.z, dt, CAMERA.AIM_TIME_CONSTANT),
      );

      roll = damp(roll, state.roll * CAMERA.ROLL_FOLLOW, dt, CAMERA.ROLL_TIME_CONSTANT);

      look();
    },

    /**
     * Place the camera at its settled position immediately, with no easing.
     *
     * Called once before the first frame. Without it the camera would start at the world
     * origin and the session would open with an unrequested swoop into position.
     *
     * @param {object} state
     * @param {{x: number, y: number, z: number}} [velocity] - Aircraft velocity, so the
     *   forward lead starts already settled rather than winding up over the first second.
     */
    snapTo(state, velocity = NO_VELOCITY) {
      lead.set(velocity.x, 0, velocity.z);

      idealPositionFor(state, ideal);
      liftClearOfGround(ideal);
      position.copy(ideal);

      /*
        Settled means the aim sits on the aircraft. It is the *target* that is ahead; the lag
        is what brings the aim back onto the aircraft in steady flight.
      */
      aim.set(state.x, state.y, state.z);
      roll = state.roll * CAMERA.ROLL_FOLLOW;

      look();
    },

    /** Current camera position. Exposed for debug readouts and tests. */
    getPosition() {
      return { x: position.x, y: position.y, z: position.z };
    },

    /**
     * Change the downward tilt while flying.
     *
     * Takes effect on the next rendered frame with no easing, which is what makes it usable for finding a preference by
     * experiment — a damped transition would make small steps hard to judge against each other.
     *
     * @param {number} degrees - Clamped to the configured range.
     * @returns {number} The tilt actually applied, so a caller can persist and display the clamped value.
     */
    setPitchDown(degrees) {
      downTilt = clampPitchDown(degrees);
      return downTilt;
    },

    /** Current downward tilt in degrees. */
    getPitchDown() {
      return downTilt;
    },
  };
}
